import { Logo } from './Logo'

/**
 * App header — the logo-lander variant. Single fixed bar with a surface variant
 * for status-specific shells.
 *
 * Behaviour:
 * - Fixed to the top of the viewport.
 * - Centered EDF logo (recolored to the on-dark color via className override).
 * - Extends UP into the status-bar safe area: the surface background fills the
 *   notch/status-bar region, and content is padded down by the inset so the
 *   logo clears the OS clock/battery icons.
 *
 * ⚠️ REQUIRES viewport-fit=cover. env(safe-area-inset-top) only returns a real
 *    value when index.html has:
 *      <meta name="viewport"
 *            content="width=device-width, initial-scale=1, viewport-fit=cover" />
 *    Without it the inset reads 0 and the header won't extend into the status
 *    bar (it'll still render fine, just flush to the top with no extra inset).
 *
 * Because the header is fixed, give your page content top padding so it isn't
 * hidden beneath the bar — see <HeaderSpacer/> below.
 */

const SAFE_TOP = 'env(safe-area-inset-top, 0px)'
const BAR_HEIGHT = 56 // px, content row height below the safe area

/**
 * Total height the fixed header occupies (safe-area inset + bar). Use this to
 * offset anything sticky that must pin below the header.
 */
export const HEADER_OFFSET = `calc(${SAFE_TOP} + ${BAR_HEIGHT}px)`

type HeaderSurface = 'dark' | 'light'

const HEADER_SURFACE_CLASS: Record<HeaderSurface, string> = {
  dark: 'bg-gray-900',
  light: 'bg-white',
}

export function Header({ surface = 'dark' }: { surface?: HeaderSurface }) {
  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 ${HEADER_SURFACE_CLASS[surface]}`}
      style={{ paddingTop: SAFE_TOP }}
    >
      <div
        className="flex items-center justify-center"
        style={{ height: BAR_HEIGHT }}
      >
        {/* Logo is secondary-colored by default; on the dark bar we keep that
            brand orange, which matches the Figma. Override via className if a
            future surface needs a different treatment. */}
        <Logo />
      </div>
    </header>
  )
}

/**
 * Spacer to offset fixed-header overlap. Render once at the top of a screen's
 * scroll content so the first content isn't hidden under the Header. Its height
 * mirrors the header: safe-area inset + bar height.
 */
export function HeaderSpacer() {
  return (
    <div
      aria-hidden
      style={{ height: HEADER_OFFSET }}
    />
  )
}
